/**
 * Version: V0.0.3.1
 * Created: 2026-06-12
 * Updated: 2026-06-13
 * Description: Language context provider that dynamically loads translations from public JSON files.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

// Utility functions for cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const hostname = window.location.hostname;
  
  // Clear any existing host-only cookie first to avoid duplicate/stale cookie conflicts
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  
  // Set the wildcard cookie
  const domainAttr = hostname === 'localhost' ? '' : (hostname.endsWith('.localhost') ? '; domain=.localhost' : (hostname.split('.').length > 1 ? `; domain=.${hostname.split('.').slice(-2).join('.')}` : ''));
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domainAttr}; SameSite=Lax`;
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = getCookie('dentiefy-lang');
    if (saved && ['en', 'fr', 'ar'].includes(saved)) {
      return saved;
    }
    
    // Auto detect from system/browser language
    try {
      const systemLang = navigator.language || navigator.userLanguage || 'en';
      const short = systemLang.split('-')[0].toLowerCase();
      if (['en', 'fr', 'ar'].includes(short)) {
        return short;
      }
    } catch (e) {
      // Ignore
    }
    return 'en';
  });

  const [translations, setTranslations] = useState({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setCookie('dentiefy-lang', language);
    // Language and RTL handling
    document.documentElement.lang = language;
    if (language === 'ar') {
      document.documentElement.dir = 'rtl';
      document.body.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.dir = 'ltr';
      document.body.setAttribute('dir', 'ltr');
    }

    // Load single translation file dynamically
    const loadTranslations = async () => {
      setIsReady(false);
      try {
        const response = await fetch(`/locales/${language}/translation.json`);
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const data = await response.json();
        setTranslations(data);
      } catch (e) {
        console.error(`Failed to load translation file for ${language}:`, e);
      } finally {
        setIsReady(true);
      }
    };

    loadTranslations();
  }, [language]);

  const setLanguage = (lang) => {
    if (['en', 'fr', 'ar'].includes(lang)) {
      setLanguageState(lang);
    }
  };

  const t = (key, fallback) => {
    return translations[key] || fallback || key;
  };

  if (!isReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3 bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
